import React from 'react';
import { useQuery } from 'react-query';
import { adminAPI } from '../../services/api';
import { Building2, Camera, Image, Play } from 'lucide-react';
import LoadingSpinner from '../../components/LoadingSpinner';

const AdminOverview = () => {
  const { data: stats, isLoading, error } = useQuery('admin-stats', adminAPI.getStats);

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <LoadingSpinner className="h-64" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Failed to load statistics. Please try again.</p>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      name: 'Companies',
      value: stats?.data?.companies || 0,
      icon: Building2,
      color: 'bg-blue-500',
      description: 'Total registered companies'
    },
    {
      name: 'Cameras',
      value: stats?.data?.cameras || 0,
      icon: Camera,
      color: 'bg-green-500',
      description: 'Total cameras in system'
    },
    {
      name: 'Photos',
      value: stats?.data?.photos || 0,
      icon: Image,
      color: 'bg-purple-500',
      description: 'Total photos uploaded'
    },
    {
      name: 'Active Jobs',
      value: stats?.data?.activeJobs || 0,
      icon: Play,
      color: 'bg-orange-500',
      description: 'Currently processing timelapses'
    },
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Admin Overview</h1>
        <p className="mt-2 text-gray-600">
          Monitor your timelapse management system
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat) => (
          <div key={stat.name} className="card p-6">
            <div className="flex items-center">
              <div className={`${stat.color} p-3 rounded-lg`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
            </div>
            <p className="mt-3 text-sm text-gray-500">{stat.description}</p>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <a
              href="/admin/companies"
              className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center">
                <Building2 className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="font-medium text-gray-900">Manage Companies</p>
                  <p className="text-sm text-gray-500">Add, edit, or remove companies</p>
                </div>
              </div>
            </a>
            <a
              href="/admin/cameras"
              className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center">
                <Camera className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="font-medium text-gray-900">Manage Cameras</p>
                  <p className="text-sm text-gray-500">Configure and assign cameras</p>
                </div>
              </div>
            </a>
          </div>
        </div>

        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">System Status</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Database</span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Online
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">API Server</span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Online
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">FFmpeg Service</span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Online
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOverview;
